/**
 * TikTok OAuth Callback Handler
 * Handles the redirect from TikTok OAuth and exchanges code for tokens.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle, Music } from 'lucide-react';
import { apiClient, ApiError } from '@/lib/api/client';
import { Button } from '@/components/ui/button';

export default function TikTokCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Connecting your TikTok account...');

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    const isPopup = window.opener && window.opener !== window;

    if (error) {
      setStatus('error');
      setMessage(errorDescription || 'Authorization was denied or cancelled');

      if (isPopup) {
        window.opener.postMessage({
          type: 'TIKTOK_OAUTH_ERROR',
          error: errorDescription || 'Authorization was denied or cancelled',
        }, window.location.origin);
        window.close();
      }
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('No authorization code received');

      if (isPopup) {
        window.opener.postMessage({
          type: 'TIKTOK_OAUTH_ERROR',
          error: 'No authorization code received',
        }, window.location.origin);
        window.close();
      }
      return;
    }

    try {
      const storedRedirectUri = localStorage.getItem('tiktok_oauth_redirect_uri');
      const redirectUri = storedRedirectUri || `${window.location.origin}/tiktok-callback`;
      const codeVerifier = localStorage.getItem('tiktok_oauth_code_verifier');

      // Clean up stored values
      localStorage.removeItem('tiktok_oauth_redirect_uri');
      localStorage.removeItem('tiktok_oauth_code_verifier');

      const response = await apiClient.post('/tiktok/oauth/exchange', {
        code,
        redirectUri,
        codeVerifier: codeVerifier || undefined,
      });

      if (response.success && response.data) {
        setStatus('success');
        const displayName = response.data.displayName || response.data.username || 'your TikTok account';
        setMessage(`Successfully connected @${displayName}!`);

        if (isPopup) {
          window.opener.postMessage({
            type: 'TIKTOK_OAUTH_SUCCESS',
            data: response.data,
          }, window.location.origin);

          setTimeout(() => {
            window.close();
          }, 1000);
        } else {
          setTimeout(() => {
            navigate('/home/social-media/tiktok');
          }, 1500);
        }
      } else {
        throw ApiError.fromResult(response, 'Invalid response from server');
      }
    } catch (error: any) {
      setStatus('error');
      const errorMessage =
        error.response?.data?.error || error.message || 'Failed to connect TikTok account';
      setMessage(errorMessage);

      if (isPopup) {
        window.opener.postMessage({
          type: 'TIKTOK_OAUTH_ERROR',
          error: errorMessage,
        }, window.location.origin);

        setTimeout(() => {
          window.close();
        }, 2000);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-pink-50 to-cyan-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="bg-black p-4 rounded-full text-white inline-block">
              <Music className="h-8 w-8" />
            </div>
          </div>
          <CardTitle>TikTok Connection</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-pink-500" />
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
              <p className="text-lg font-semibold text-green-700">{message}</p>
              <p className="text-sm text-muted-foreground">
                {window.opener && window.opener !== window
                  ? 'Closing popup...'
                  : 'Redirecting...'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-red-500" />
              <p className="text-lg font-semibold text-red-700">Connection Failed</p>
              <p className="text-sm text-muted-foreground">{message}</p>
              <div className="flex gap-2 justify-center mt-4">
                <Button
                  onClick={() => navigate('/home/social-media/tiktok')}
                  variant="outline"
                >
                  Go Back
                </Button>
                <Button onClick={handleCallback}>Try Again</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
